import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Coins, Send } from "lucide-react";
import { api, errMsg, imgSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TipDialog } from "@/components/MoneyDialogs";

function ConversationList({ activeId }) {
  const [convos, setConvos] = useState([]);
  useEffect(() => { api.get("/conversations").then((r) => setConvos(r.data)); }, [activeId]);
  return (
    <aside className={`border-r border-white/5 ${activeId ? "hidden md:block" : ""}`} data-testid="conversation-list">
      <p className="eyebrow p-5">Conversations</p>
      {convos.length === 0 && <p className="px-5 text-sm text-zinc-500">No messages yet. Find someone in <Link to="/discover" className="text-red-500">Discover</Link>.</p>}
      {convos.map((c) => (
        <Link key={c.user.id} to={`/messages/${c.user.id}`} data-testid={`conversation-${c.user.id}`}
          className={`flex items-center gap-3 px-5 py-4 transition-colors hover:bg-white/5 ${activeId === c.user.id ? "bg-white/5" : ""}`}>
          <img src={imgSrc(c.user.photo)} alt="" className="h-11 w-11 rounded-full object-cover" />
          <div className="min-w-0"><p className="font-medium">{c.user.name}</p><p className="truncate text-xs text-zinc-500">{c.last_text}</p></div>
        </Link>
      ))}
    </aside>
  );
}

function Thread({ otherId }) {
  const { user } = useAuth();
  const [other, setOther] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const end = useRef(null);
  const load = () => api.get(`/messages/${otherId}`).then((r) => setMsgs(r.data));
  useEffect(() => {
    api.get(`/members/${otherId}`).then((r) => setOther(r.data));
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherId]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { await api.post(`/messages/${otherId}`, { text }); setText(""); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  if (!other) return <div />;
  return (
    <section className="flex h-[calc(100vh-4rem)] flex-col" data-testid="message-thread">
      <div className="flex items-center justify-between border-b border-white/5 p-4">
        <Link to={`/members/${other.id}`} className="flex items-center gap-3"><img src={imgSrc(other.photo)} alt="" className="h-10 w-10 rounded-full object-cover" /><span className="font-display text-2xl">{other.name}</span></Link>
        <TipDialog to={other} onTipped={load} trigger={<button data-testid="thread-tip-btn" className="flex items-center gap-1 rounded-full border border-amber-500/40 px-4 py-2 text-xs text-amber-300 hover:bg-amber-500/10"><Coins className="h-4 w-4" />Tip</button>} />
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-5">
        {msgs.map((m) => (
          <div key={m.id} className={`flex ${m.from_id === user.id ? "justify-end" : ""}`}>
            <p data-testid="message-bubble" className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${m.kind === "tip" ? "border border-amber-500/40 bg-amber-500/10 text-amber-200" : m.from_id === user.id ? "bg-red-600 text-white" : "bg-zinc-800"}`}>{m.text}</p>
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="flex gap-2 border-t border-white/5 p-4">
        <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Write something charming…" className="flex-1 rounded-full bg-zinc-900 px-5 py-3 text-sm outline-none focus:ring-1 focus:ring-red-600" />
        <button data-testid="message-send-btn" className="btn-red rounded-full px-5"><Send className="h-4 w-4" /></button>
      </form>
    </section>
  );
}

export default function Messages() {
  const { id } = useParams();
  return (
    <main className="mx-auto grid max-w-7xl md:grid-cols-[320px_1fr]" data-testid="messages-page">
      <ConversationList activeId={id} />
      {id ? <Thread otherId={id} /> : <div className="hidden items-center justify-center text-zinc-600 md:flex">Select a conversation</div>}
    </main>
  );
}
